<script lang="ts">
import {authFetch} from '../Auth'
import {onDestroy, onMount} from 'svelte'

interface QrSession {
    session_key: string
    created_at: number
    qr_link: string | null
    qr_image_base64: string | null
    wait_qr: boolean
    success: boolean
    wait_password: boolean
    telegram_id: string | null
}

interface QrAttempt {
    id: string
    qr_link_at: string | null
    success_at: string | null
    phone: string | null
}

let firstName: string = ''
let loading: boolean = false
let session: QrSession | null = null
let error: string | null = null
let pollInterval: ReturnType<typeof setInterval> | null = null

let attempts: QrAttempt[] = []
let attemptsLoading: boolean = false

async function startQrLogin() {
    loading = true
    error = null
    session = null
    clearPoll()

    let response = await authFetch('/qr-login/start', {
        method: 'POST',
        body: JSON.stringify({first_name: firstName || null})
    })

    if (!response.ok) {
        let body = await response.json().catch(() => ({}))
        error = body.error ?? `Помилка: ${response.status}`
        loading = false
        return
    }

    session = await response.json()
    loading = false

    if (session && !session.success && !session.wait_password) {
        startPoll()
    }

    loadAttempts()
}

function startPoll() {
    pollInterval = setInterval(pollStatus, 3000)
}

function clearPoll() {
    if (pollInterval !== null) {
        clearInterval(pollInterval)
        pollInterval = null
    }
}

async function pollStatus() {
    if (!session) return
    let response = await authFetch(`/qr-login/status/${session.session_key}`)
    if (!response.ok) return
    let updated: QrSession = await response.json()
    session = updated
    if (session.success || session.wait_password) {
        clearPoll()
        loadAttempts()
    }
}

async function loadAttempts() {
    attemptsLoading = true
    try {
        const res = await authFetch('/qr-login/attempts')
        if (res.ok) {
            attempts = await res.json()
        }
    } finally {
        attemptsLoading = false
    }
}

onMount(loadAttempts)
onDestroy(clearPoll)

function formatDate(val: string | null): string {
    if (!val) return '—'
    return new Date(val).toLocaleString('uk-UA')
}
</script>

<div class="container-fluid mt-3">
    <div class="row justify-content-center">
        <div class="col-md-6 col-lg-5">
            <h4 class="mb-3">Авторизація через QR-код</h4>

            <div class="mb-3">
                <label class="form-label">Ім'я акаунту (необов'язково)</label>
                <input
                    class="form-control"
                    type="text"
                    placeholder="Ім'я для нового акаунту"
                    bind:value={firstName}
                    disabled={loading}
                />
            </div>

            <button class="btn btn-primary" on:click={startQrLogin} disabled={loading}>
                {#if loading}
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Очікування QR-коду...
                {:else}
                    <i class="bi bi-qr-code me-1"></i>
                    Отримати QR-код
                {/if}
            </button>

            {#if error}
                <div class="alert alert-danger mt-3">{error}</div>
            {/if}

            {#if session}
                {#if session.wait_qr && session.qr_image_base64}
                    <div class="mt-4 text-center">
                        <p class="text-muted mb-2">Відскануйте QR-код у додатку Telegram</p>
                        <img
                            src="data:image/png;base64,{session.qr_image_base64}"
                            alt="QR код Telegram"
                            class="img-fluid"
                            style="max-width: 256px; border: 1px solid #dee2e6; border-radius: 8px; padding: 8px;"
                        />
                        <p class="text-muted mt-2 small">
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Очікування сканування...
                        </p>
                    </div>
                {/if}

                {#if session.success}
                    <div class="alert alert-success mt-3">
                        <i class="bi bi-check-circle-fill me-1"></i>
                        <strong>Авторизація успішна!</strong>
                        {#if session.telegram_id}
                            <div class="mt-1 small">Telegram ID: {session.telegram_id}</div>
                        {/if}
                    </div>
                {/if}

                {#if session.wait_password}
                    <div class="alert alert-warning mt-3">
                        <i class="bi bi-shield-lock me-1"></i>
                        <strong>Потрібен пароль двофакторної авторизації.</strong>
                        <div class="text-muted mt-1 small">Введіть хмарний пароль у полі авторизації Telegram на своєму пристрої.</div>
                    </div>
                {/if}
            {/if}
        </div>
    </div>

    <div class="row mt-4">
        <div class="col-12">
            <h5 class="mb-2">
                QR-спроби
                {#if attemptsLoading}
                    <span class="spinner-border spinner-border-sm ms-2" role="status"></span>
                {/if}
            </h5>
            {#if attempts.length === 0 && !attemptsLoading}
                <p class="text-muted">Спроб не знайдено.</p>
            {:else}
                <table class="table table-sm table-bordered table-hover">
                    <thead class="table-light">
                        <tr>
                            <th>#</th>
                            <th>ID</th>
                            <th>Телефон</th>
                            <th>Час генерації QR</th>
                            <th>Успіх</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each attempts as attempt, i}
                            <tr>
                                <td>{attempts.length - i}</td>
                                <td>{attempt.id}</td>
                                <td>{attempt.phone ?? '—'}</td>
                                <td>{formatDate(attempt.qr_link_at)}</td>
                                <td>
                                    {#if attempt.success_at}
                                        <span class="text-success">{formatDate(attempt.success_at)}</span>
                                    {:else}
                                        <span class="text-muted">—</span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {/if}
        </div>
    </div>
</div>
